import type { HtmlAssetDescription } from '../types';

/** The generated-graphic-specific fields of a serialized asset description. */
export type HtmlCompositionDescription = Pick<HtmlAssetDescription, 'parts' | 'texts' | 'status'>;

/**
 * The copy an element renders as its own part: everything it contains except the copy of nested
 * labeled parts, which report themselves. Inline pieces stay in — a headline split into word spans,
 * or a line with one emphasized word, is a single part whose copy is the whole line.
 */
function ownText(element: Element): string {
    const clone = element.cloneNode(true) as Element;
    for (const nested of Array.from(clone.querySelectorAll('[data-codeo-asset]'))) {
        nested.remove();
    }
    return (clone.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Describe a composition as the parts an agent can address and the copy they show, never the
 * document itself — `read_html_asset` is how that is fetched, once, rather than on every turn.
 */
export function describeHtmlComposition(html: string): HtmlCompositionDescription {
    if (!html) {
        return { status: 'empty (awaiting generation)' };
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const labeled = Array.from(doc.querySelectorAll('[data-codeo-asset]')).filter((element) =>
        Boolean(element.getAttribute('data-codeo-asset')),
    );
    const parts = labeled.map((element) => element.getAttribute('data-codeo-asset') ?? '');
    const texts = labeled.map(ownText).filter(Boolean);
    return {
        ...(parts.length ? { parts: parts.join(', ') } : {}),
        ...(texts.length ? { texts: texts.join(' | ') } : {}),
    };
}
